import {
	AVAILABILITY_STATUS,
	type AvailabilityFetchResponse,
	fetchAvailability,
} from '@api/availability';
import type { AvailabilityResponse } from '@components/calendar/types';
import { CALENDAR_RANGE_YEARS } from '@constants/calendar';
import { Logger } from '@utils/logger/logger';
import { addMonths, addYears, startOfMonth } from 'date-fns';
import { atom, type createStore } from 'jotai';
import {
	ContentGatingStatus,
	availabilityContentGateAtom,
} from '../../../../jotai/storefront/content-gating';

export const FETCH_STATUS = {
	OK: 'OK',
	NONE: 'NONE',
	RETRY: 'RETRY',
} as const;

interface AvailabilityState {
	data: AvailabilityResponse | null;
	loading: boolean;
	fetchStatus: keyof typeof FETCH_STATUS | null;
}

const initialState: AvailabilityState = {
	data: null,
	loading: true,
	fetchStatus: null,
};

export const availabilityDataAtom = atom(initialState.data);
export const availabilityLoading = atom(initialState.loading);
export const availabilityFetchStatus = atom(initialState.fetchStatus);

type FetchAvailabilityParams = {
	vendorId: string;
};

const availabilityResponseAtom = atom(
	null,
	(_get, set, { status, response }: AvailabilityFetchResponse) => {
		if (status === AVAILABILITY_STATUS.OK) {
			set(availabilityDataAtom, response);
		} else if (status === AVAILABILITY_STATUS.NOT_CONFIGURED) {
			set(availabilityDataAtom, null);
			Logger.log({
				log_message: 'Vendor has not configured their availability.',
			});
		} else {
			set(availabilityDataAtom, null);
		}
	},
);

const getAvailability = async (
	vendorId: string,
	endDate: Date,
	handleResponse: (response: AvailabilityFetchResponse) => void,
) => {
	const response = await fetchAvailability({
		storefrontId: vendorId,
		startDate: new Date(),
		endDate: endDate,
	});
	handleResponse(response);

	return response;
};

export const handleAvailabilityResponseAtom = atom(
	null,
	(_get, set, response: AvailabilityFetchResponse) => {
		set(availabilityResponseAtom, response);
		set(availabilityLoading, false);
		set(
			availabilityFetchStatus,
			response.status === AVAILABILITY_STATUS.OK
				? FETCH_STATUS.OK
				: FETCH_STATUS.NONE,
		);
	},
);

export const fetchAvailabilityAtom = atom<
	null,
	[value: FetchAvailabilityParams],
	void
>(null, async (get, set, value) => {
	const contentGatedStatus = get(availabilityContentGateAtom);
	if (contentGatedStatus === ContentGatingStatus.BLOCKED) return;

	try {
		if (
			get(availabilityFetchStatus) === FETCH_STATUS.RETRY ||
			get(availabilityFetchStatus) === null
		) {
			set(availabilityLoading, true);
			await getAvailability(
				value.vendorId,
				startOfMonth(addMonths(new Date(), 1)),
				(response: AvailabilityFetchResponse) =>
					set(handleAvailabilityResponseAtom, response),
			);
		}

		if (get(availabilityFetchStatus) === FETCH_STATUS.OK) {
			await getAvailability(
				value.vendorId,
				addYears(new Date(), CALENDAR_RANGE_YEARS),
				(response: AvailabilityFetchResponse) =>
					set(handleAvailabilityResponseAtom, response),
			);
		}
	} catch (error) {
		Logger.error({
			log_message: 'Error fetching availability',
			errorToLog: error,
			newRelic: true,
		});
		set(availabilityDataAtom, null);
		set(availabilityLoading, false);
		set(availabilityFetchStatus, FETCH_STATUS.NONE);
	}
});

export const getAvailabilityDataToHydrate = (
	store: ReturnType<typeof createStore>,
) => {
	return {
		availabilityData: store.get(availabilityDataAtom),
		availabilityLoading: store.get(availabilityLoading),
		availabilityFetchStatus: store.get(availabilityFetchStatus),
	};
};

export const getAvailabilityInitialValues = (
	values: ReturnType<typeof getAvailabilityDataToHydrate>,
) => {
	const {
		availabilityData: data,
		availabilityLoading: loading,
		availabilityFetchStatus: fetchStatus,
	} = values || {};
	return [
		[availabilityDataAtom, data ?? initialState.data],
		[availabilityLoading, loading ?? initialState.loading],
		[availabilityFetchStatus, fetchStatus ?? initialState.fetchStatus],
	];
};
